import { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ForecastMapProvider } from './ForecastMapContext';

// Re-use the default Leaflet marker assets that ship with the package. Without
// this, marker icons disappear when the app is bundled (a known Leaflet quirk).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const DEFAULT_CENTER = [44.0, 20.9]; // rough Serbia center, used when no pin
const OVERVIEW_ZOOM = 8;  // country view — when no pin is set
const PIN_ZOOM = 13;      // local detail — when a lat/lng is provided

// Bridge from react-leaflet's useMap into the page's context so future overlay
// children can grab the map instance with useForecastMap().
const MapInstanceExposer = ({ onReady }) => {
    const map = useMap();
    useEffect(() => { onReady(map); }, [map, onReady]);
    return null;
};

const ClickHandler = ({ onLocationPick }) => {
    useMapEvents({
        click: (e) => onLocationPick({ lat: e.latlng.lat, lng: e.latlng.lng }),
    });
    return null;
};

const RecenterOnPin = ({ pin }) => {
    const map = useMap();
    useEffect(() => {
        if (!pin) return;
        // If the user is at a wider-than-detail zoom (e.g. fresh page load with
        // no pin, then they pick a saved spot), zoom IN to PIN_ZOOM. Otherwise
        // just pan — preserves zoom continuity when hopping between pins.
        if (map.getZoom() < PIN_ZOOM) {
            map.setView([pin.lat, pin.lng], PIN_ZOOM);
        } else {
            map.panTo([pin.lat, pin.lng]);
        }
    }, [pin, map]);
    return null;
};

// Leaflet caches the container size at init time and doesn't re-measure on
// resize, so the right-hand tiles go blank when the map container grows (e.g.
// after the right panel closes and the map column expands back to flex-1).
// A ResizeObserver on the map's container tells Leaflet to re-measure.
const InvalidateOnResize = () => {
    const map = useMap();
    useEffect(() => {
        const container = map.getContainer();
        if (!container || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => {
            map.invalidateSize();
        });
        ro.observe(container);
        return () => ro.disconnect();
    }, [map]);
    return null;
};

const ForecastMap = ({ pin = null, onLocationPick = () => {}, children = null }) => {
    const [mapInstance, setMapInstance] = useState(null);

    const initialCenter = useMemo(
        () => (pin ? [pin.lat, pin.lng] : DEFAULT_CENTER),
        // Initial center + zoom are read once on mount; later changes flow
        // through RecenterOnPin (pan only, preserves the user's zoom).
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    const initialZoom = useMemo(
        () => (pin ? PIN_ZOOM : OVERVIEW_ZOOM),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <div className="w-full h-full relative isolate" data-testid="forecast-map">
            <MapContainer
                center={initialCenter}
                zoom={initialZoom}
                style={{ width: '100%', height: '100%' }}
                scrollWheelZoom
            >
                <TileLayer
                    attribution='Map data: © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: © <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)'
                    url="https://tile.opentopomap.org/{z}/{x}/{y}.png"
                    maxZoom={17}
                />
                <ClickHandler onLocationPick={onLocationPick} />
                <RecenterOnPin pin={pin} />
                <InvalidateOnResize />
                <MapInstanceExposer onReady={setMapInstance} />
                {pin && <Marker position={[pin.lat, pin.lng]} />}
                <ForecastMapProvider value={mapInstance}>
                    {children}
                </ForecastMapProvider>
            </MapContainer>
        </div>
    );
};

export default ForecastMap;
